export type StatusConfigResource =
  | 'sites'
  | 'device-types'
  | 'devices'
  | 'interfaces'
  | 'collection-checks'
  | 'device-components'
  | 'links'
  | 'telemetry-bindings'
  | 'device-type-snmp-overlays'
  | 'snmp-auth-profiles';

export type WritableStatusConfigResource = Exclude<StatusConfigResource, 'device-components' | 'collection-checks'>;

export interface StatusConfigListOptions {
  limit?: number;
  offset?: number;
  page?: number;
  pageSize?: number;
  q?: string;
  sort?: string;
  filters?: Record<string, string | number | boolean | null | undefined>;
}

export interface StatusConfigListResponse<T> {
  tenant_id: string;
  resource: StatusConfigResource;
  items: T[];
  total?: number;
  limit?: number;
  offset?: number;
  page?: number;
  page_size?: number;
  sort?: string;
  q?: string;
  filters?: Record<string, string>;
}

export interface StatusValidationIssue {
  field: string;
  code: string;
  message: string;
}

export class StatusInventoryError extends Error {
  status: number;
  issues: StatusValidationIssue[];

  constructor(message: string, status: number, issues: StatusValidationIssue[] = []) {
    super(message);
    this.name = 'StatusInventoryError';
    this.status = status;
    this.issues = issues;
  }
}

export interface StatusSite {
  site_id: string;
  tenant_id: string;
  external_ref?: string;
  slug: string;
  display_name: string;
  location: { lat: number; lng: number };
  metadata?: Record<string, unknown>;
}

export interface StatusDeviceType {
  metadata?: Record<string, unknown>;
  active_overlay_count?: number;
  device_type_id: string;
  tenant_id?: string;
  scope?: 'system' | 'tenant';
  read_only?: boolean;
  slug: string;
  display_name: string;
  vendor?: string;
  model_match?: string;
  default_monitoring_enabled: boolean;
}

export interface StatusDevice {
  site_display_name?: string;
  device_type_display_name?: string;
  active_overlay_count?: number;
  collection_state?: string;
  last_success_at?: string;
  device_id: string;
  tenant_id: string;
  site_id: string;
  device_type_id?: string;
  external_ref?: string;
  hostname: string;
  display_name?: string;
  device_role?: string;
  vendor?: string;
  model?: string;
  management_address?: string;
  monitoring_enabled: boolean;
  metadata?: Record<string, unknown>;
}

export interface StatusDeviceDiscoveryPayload {
  site_id: string;
  device_type_id?: string;
  hostname: string;
  display_name?: string;
  management_address?: string;
  device_role?: string;
  snmp_auth?: string;
  snmp_walk_params?: string;
  snmp_version?: string;
  snmp_community?: string;
  snmp_port?: string;
  monitoring_enabled?: boolean;
  metadata?: Record<string, unknown>;
  identity?: {
    sys_descr?: string;
    sys_object_id?: string;
  };
  preview_only?: boolean;
}

export interface StatusDeviceDiscoveryResult {
  matched: boolean;
  identity: {
    sys_descr?: string;
    sys_object_id?: string;
  };
  device_type?: StatusDeviceType;
  device?: StatusDevice;
  inventory?: {
    profile: string;
    interfaces_discovered: number;
    components_discovered?: number;
    sensors_discovered?: number;
    warnings?: string[];
  };
}

export interface StatusInterface {
  interface_id: string;
  tenant_id: string;
  device_id: string;
  name: string;
  ifindex?: number;
  description?: string;
  monitoring_enabled: boolean;
}

export interface StatusDeviceComponent {
  component_id: string;
  tenant_id: string;
  device_id: string;
  interface_id?: string;
  external_ref?: string;
  component_key: string;
  parent_component_key?: string;
  name: string;
  component_kind?: string;
  sensor_type?: string;
  sensor_unit?: string;
  sensor_scale?: string;
  sensor_precision?: number;
  sensor_value?: number;
  sensor_oper_status?: number;
  sensor_normalized_value?: number;
  sensor_normalized_unit?: string;
  sensor_health?: string;
  sensor_observed_at?: string;
  sensor_stale?: boolean;
  monitoring_enabled: boolean;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface StatusLinkEndpoint {
  role: string;
  site_id: string;
  device_id?: string;
  interface_id?: string;
  display_name?: string;
  site_label?: string;
  device_label?: string;
  interface_label?: string;
}

export interface StatusLinkConfig {
  link_id: string;
  tenant_id: string;
  external_ref?: string;
  slug: string;
  display_name: string;
  description?: string;
  link_kind?: string;
  capacity_bps?: number;
  monitoring_enabled: boolean;
  source: StatusLinkEndpoint;
  target: StatusLinkEndpoint;
  health?: string;
  current_health?: string;
  lifecycle?: 'active' | 'suppressed';
  suppressed?: boolean;
  discovery_managed?: boolean;
}

export interface StatusDeviceTypeSNMPOverlay {
  device_type_snmp_overlay_id: string;
  tenant_id?: string;
  scope?: 'system' | 'tenant';
  read_only?: boolean;
  device_type_id: string;
  overlay_name: string;
  module_name: string;
  overlay_config: Record<string, unknown>;
  version: number;
  active: boolean;
}

export interface StatusSNMPAuthProfile {
  snmp_auth_profile_id: string;
  tenant_id: string;
  auth_name: string;
  display_name: string;
  snmp_version: number;
  username?: string;
  security_level?: string;
  auth_protocol?: string;
  priv_protocol?: string;
  active: boolean;
  has_community: boolean;
  has_auth_password: boolean;
  has_priv_password: boolean;
  metadata?: Record<string, unknown>;
}

export interface StatusTenantLink {
  id: string;
  slug: string;
  display_name: string;
  state?: {
    health?: string;
    oper_state?: string;
  };
}

export interface StatusTenantLinksResponse {
  links: StatusTenantLink[];
}

export interface StatusManagedServiceSummary {
  expected_check_count: number;
  stale_check_count: number;
  affected_device_count: number;
  tenant_id: string;
  service_name: string;
  offering: string;
  deployment_mode: string;
  substrate: string;
  control_plane_mode: string;
  lifecycle_state: string;
  health_state: string;
  telemetry_backend_state: string;
  telemetry_backend_id?: string;
  telemetry_backend_name?: string;
  telemetry_backend_diagnostic?: string;
  egress_identity_provider: string;
  egress_identity_purpose: string;
  capabilities: string[];
  generated_at?: string;
  config_hash?: string;
  target_count: number;
  auth_profile_count: number;
  overlay_module_count: number;
  ready_device_count: number;
  not_ready_device_count: number;
  healthy_module_count: number;
  unhealthy_module_count: number;
  awaiting_module_count: number;
  device_readiness_errors?: string[];
  last_observed_at?: string;
}

export interface StatusTopologyListOptions {
  limit?: number;
  offset?: number;
  page?: number;
  pageSize?: number;
  q?: string;
  sort?: string;
}

export interface StatusTopologyEvidenceListOptions extends StatusTopologyListOptions {
  evidenceType?: string;
  direction?: string;
  linkId?: string;
  sourceDeviceId?: string;
  sourceInterfaceId?: string;
}

export interface StatusTopologyConflictListOptions extends StatusTopologyListOptions {
  conflictType?: string;
  severity?: string;
  active?: boolean;
}

export interface StatusTopologyListResponse<T> {
  tenant_id: string;
  server_time?: string;
  items: T[];
  total?: number;
  limit?: number;
  offset?: number;
  sort?: string;
  q?: string;
  filters?: Record<string, string>;
}

export interface StatusTopologyEvidence {
  evidence_id: string;
  link_id?: string;
  evidence_type: string;
  direction: string;
  source_device_id?: string;
  source_interface_id?: string;
  observed_at: string;
  raw_reference?: string;
  raw?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface StatusTopologyConflict {
  conflict_id: string;
  severity: string;
  conflict_type: string;
  description: string;
  involved_devices?: string[];
  involved_interfaces?: string[];
  evidence_ids?: string[];
  detected_at: string;
  resolved_at?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

async function requestJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {})
    },
    ...init
  });

  if (!response.ok) {
    const payload = await response.text();
    let message = payload || `Request failed: ${response.status}`;
    let issues: StatusValidationIssue[] = [];
    try {
      const parsed = JSON.parse(payload) as { error?: string; validation_errors?: StatusValidationIssue[] };
      message = parsed.error ?? message;
      issues = parsed.validation_errors ?? [];
    } catch {
      // Preserve upstream text when it is not a structured validation response.
    }
    throw new StatusInventoryError(message, response.status, issues);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function listStatusConfig<T>(resource: StatusConfigResource, options?: StatusConfigListOptions): Promise<T[]> {
  const payload = await listStatusConfigPage<T>(resource, options);
  return payload.items ?? [];
}

export async function listStatusConfigPage<T>(resource: StatusConfigResource, options?: StatusConfigListOptions): Promise<StatusConfigListResponse<T>> {
  const query = statusConfigListQuery(options);
  const payload = await requestJSON<StatusConfigListResponse<T>>(`/api/v1/status/config/${resource}${query}`);
  return payload;
}

export async function createStatusConfig<T>(resource: WritableStatusConfigResource, payload: unknown): Promise<T> {
  return requestJSON<T>(`/api/v1/status/config/${resource}`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function updateStatusConfig<T>(resource: WritableStatusConfigResource, id: string, payload: unknown): Promise<T> {
  return requestJSON<T>(`/api/v1/status/config/${resource}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
}

export async function deleteStatusConfig(resource: WritableStatusConfigResource, id: string): Promise<void> {
  await requestJSON<void>(`/api/v1/status/config/${resource}/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
}

export async function suppressStatusLink(id: string, reason?: string): Promise<void> {
  void reason;
  await requestJSON<void>(`/api/v1/status/config/links/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function restoreStatusLink(id: string): Promise<void> {
  await requestJSON<void>(`/api/v1/status/config/links/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
}

function statusConfigListQuery(options?: StatusConfigListOptions): string {
  if (!options) {
    return '';
  }
  const params = new URLSearchParams();
  appendQueryParam(params, 'limit', options.limit);
  appendQueryParam(params, 'offset', options.offset);
  appendQueryParam(params, 'page', options.page);
  appendQueryParam(params, 'page_size', options.pageSize);
  appendQueryParam(params, 'q', options.q);
  appendQueryParam(params, 'sort', options.sort);
  for (const [field, value] of Object.entries(options.filters ?? {})) {
    appendQueryParam(params, `filter[${field}]`, value);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

function appendQueryParam(params: URLSearchParams, key: string, value: string | number | boolean | null | undefined): void {
  if (value === undefined || value === null || value === '') {
    return;
  }
  params.set(key, String(value));
}

export async function discoverStatusDevice(payload: StatusDeviceDiscoveryPayload): Promise<StatusDeviceDiscoveryResult> {
  return requestJSON<StatusDeviceDiscoveryResult>('/api/v1/status/config/devices/discover', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function listStatusTenantLinks(): Promise<StatusTenantLink[]> {
  const payload = await requestJSON<StatusTenantLinksResponse>('/api/v1/status/links');
  return payload.links ?? [];
}

export async function getStatusManagedServiceSummary(): Promise<StatusManagedServiceSummary> {
  return requestJSON<StatusManagedServiceSummary>('/api/v1/status/managed-service/status');
}

export async function listStatusTopologyEvidencePage(options?: StatusTopologyEvidenceListOptions): Promise<StatusTopologyListResponse<StatusTopologyEvidence>> {
  return requestJSON<StatusTopologyListResponse<StatusTopologyEvidence>>(`/api/v1/status/topology/evidence${statusTopologyEvidenceQuery(options)}`);
}

export async function listStatusTopologyEvidence(options?: StatusTopologyEvidenceListOptions): Promise<StatusTopologyEvidence[]> {
  const payload = await listStatusTopologyEvidencePage(options);
  return payload.items ?? [];
}

export async function listStatusTopologyConflictsPage(options?: StatusTopologyConflictListOptions): Promise<StatusTopologyListResponse<StatusTopologyConflict>> {
  return requestJSON<StatusTopologyListResponse<StatusTopologyConflict>>(`/api/v1/status/topology/conflicts${statusTopologyConflictQuery(options)}`);
}

export async function listStatusTopologyConflicts(options?: StatusTopologyConflictListOptions): Promise<StatusTopologyConflict[]> {
  const payload = await listStatusTopologyConflictsPage(options);
  return payload.items ?? [];
}

function statusTopologyEvidenceQuery(options?: StatusTopologyEvidenceListOptions): string {
  const params = statusTopologyBaseQuery(options);
  appendQueryParam(params, 'evidence_type', options?.evidenceType);
  appendQueryParam(params, 'direction', options?.direction);
  appendQueryParam(params, 'link_id', options?.linkId);
  appendQueryParam(params, 'source_device_id', options?.sourceDeviceId);
  appendQueryParam(params, 'source_interface_id', options?.sourceInterfaceId);
  const query = params.toString();
  return query ? `?${query}` : '';
}

function statusTopologyConflictQuery(options?: StatusTopologyConflictListOptions): string {
  const params = statusTopologyBaseQuery(options);
  appendQueryParam(params, 'conflict_type', options?.conflictType);
  appendQueryParam(params, 'severity', options?.severity);
  appendQueryParam(params, 'active', options?.active);
  const query = params.toString();
  return query ? `?${query}` : '';
}

function statusTopologyBaseQuery(options?: StatusTopologyListOptions): URLSearchParams {
  const params = new URLSearchParams();
  appendQueryParam(params, 'limit', options?.limit);
  appendQueryParam(params, 'offset', options?.offset);
  appendQueryParam(params, 'page', options?.page);
  appendQueryParam(params, 'page_size', options?.pageSize);
  appendQueryParam(params, 'q', options?.q);
  appendQueryParam(params, 'sort', options?.sort);
  return params;
}
